import { Alert, Link } from '@mm/components';
import { InfoCircleSolidIcon } from '@mm/icons';
import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { LinkTypes } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import checkboxPluginHelpImage from 'assets/checkbox-plugin-help.png';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../../common/hooks/use-edit-page-prime-state';
import { useShowInactiveBannerFlag } from '../../../common/hooks/use-show-inactive-bot-banner-flag';
import { titleByType } from '../../build/constants/getPromoterValue';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialogueSelectorWithReload } from '../../startBotPromoters/common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { EditStartBotHeader } from '../../startBotPromoters/common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { StartBotsDialogueEditor } from '../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { usePromoterJSCode } from '../../startBotPromoters/common/use-promoter-js-code';
import DomainsField from '../components/DomainsField/DomainsField';
import { CodeWrapper } from './CodeWrapper/CodeWrapper';
import './EditPromoterPanel.pcss';
import { EditSlug } from './EditSlug/EditSlug';
import { DialoguePreview } from '../../../../modules/DailoguePreview/DialoguePreview';

type Props = {
  kind: LinkTypes;
};

export const EditPromoterPanel = ({ kind }: Props) => {
  const { widgets, isFetching, page, promoter } = useEditPagePrimeState(kind);
  const chatbot = useSelector(activeChatbotSelector);
  const domain = useSelector(domainSelector);
  const domainCRUDActions = useDomainCRUDActions();
  const { update } = usePromoterCRUDActions();
  const promoterJSCode = usePromoterJSCode(promoter, page);

  const textTypeForAlert = useMemo(() => {
    switch (promoter?.kind) {
      case 'link':
        return 'Share Link';
      case 'send_to_messenger':
        return 'Send to Messenger Button';
      case 'checkbox':
        return 'Checkbox Plugin';
      default:
        return '';
    }
  }, [promoter?.kind]);

  const pathGraph = useNewGraphInstance(promoter?.pageId);
  const { openActivateInactiveBotModal } = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const facebookPage = useSelector(activeFacebookPageSelector);
  const [alertVisible, setAlertVisible] = useState(
    !!facebookPage?.euLimitsAt &&
      (kind === 'send_to_messenger' || kind === 'checkbox' || kind === 'link'),
  );
  const onCloseAlert = () => {
    setAlertVisible(false);
  };

  const promoterCRUDActions = usePromoterCRUDActions();
  const updatePromoter = () => {
    if (promoter) {
      promoterCRUDActions.update(chatbot.id, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };

  const customActivateAction = () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else {
        updatePromoter();
      }
    }
  };

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (savedPromoter && newDialogueId) {
          const newUrlParams = {
            chatbotId: chatbot.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          };

          const newUrl = (() => {
            switch (promoter.kind) {
              case 'link':
                return BotBuilderRoutes.shareLinkEdit.getLink(newUrlParams);
              case 'send_to_messenger':
                return BotBuilderRoutes.sendToMessengerEdit.getLink(
                  newUrlParams,
                );
              case 'checkbox':
                return BotBuilderRoutes.checkboxPluginEdit.getLink(
                  newUrlParams,
                );
              default:
                return '';
            }
          })();

          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
        forFacebook
      />
    ) : null;
  }, [promoter, chatbot.id]);

  if (promoter && page && !isFetching) {
    const updateSlug = (slug: string) => {
      update(chatbot.id, { ...promoter, slug });
    };

    const valueTitle = `Copy ${titleByType.get(kind)}`;

    const onDomainUpdate = (domainsList: string[]) => {
      const { id, connectedPageId } = domain;
      domainCRUDActions.update({ id, connectedPageId, domainsList });
    };

    return (
      <div className="edit-promoter-panel">
        <InactiveChannelAlertSmart facebook type={textTypeForAlert} />

        <EditStartBotHeader
          promoter={promoter}
          page={page}
          botIsTemplate={!!chatbot.template}
          customActivateAction={customActivateAction}
        />
        {alertVisible && (
          <Alert
            type="warning"
            className="edit-promoter-panel__warning-alert"
            closeIconProps={{ onClick: onCloseAlert }}
          >
            This feature is no longer available on Messenger due to{' '}
            <Link
              href="https://mobilemonkey.com/help/article/cg1qvuexfy-updated-messaging-policies-for-eu-pages-users"
              isBlank
            >
              EU policy changes.
            </Link>
          </Alert>
        )}

        <div className="edit-promoter-panel__content">
          <div className="edit-promoter-panel__editor">
            <CollapsibleContentContainer
              orderNumber="1"
              containerName={`Choose or create a Dialogue for visitors who ${
                kind === 'link' ? 'click your link' : 'opt in'
              }`}
            >
              <PathGraphContext.Provider value={pathGraph}>
                {dialogueSelectorDropdown}
                <StartBotsDialogueEditor
                  chatbotId={chatbot.id}
                  widgets={widgets}
                  dialogueId={promoter.pageId}
                  platforms={['facebook']}
                />
              </PathGraphContext.Provider>
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName={
                kind === 'link' ? 'Customize your Share Link' : 'Setup'
              }
            >
              {kind === 'link' ? (
                <EditSlug slug={promoter.slug} updateSlug={updateSlug} />
              ) : (
                <DomainsField
                  domains={domain.domainsList}
                  updateDomains={onDomainUpdate}
                />
              )}
              {kind === 'checkbox' && (
                <CodeWrapper
                  code={'onclick="confirmOptIn()"'}
                  label="Code to subscribe user"
                  tip="Paste this code on your submit button, "
                  helpLink={checkboxPluginHelpImage}
                  helpLinkLabel="view example."
                />
              )}
              <CodeWrapper code={promoterJSCode} label={valueTitle} />
              {kind === 'link' && (
                <div className="edit-promoter-panel__message-info-block">
                  <InfoCircleSolidIcon />
                  <div>
                    {'Share Links will open up in Messenger. '}
                    <a
                      className="edit-promoter-panel__info-block-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={promoterJSCode}
                    >
                      Click here to preview your share link.
                    </a>
                  </div>
                </div>
              )}
            </CollapsibleContentContainer>
          </div>
          {promoter.pageId && (
            <div className="edit-promoter-panel__preview">
              <DialoguePreview
                dialogueId={promoter?.pageId}
                style={{ top: -20, marginTop: 20 }}
                sticky
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};
